import * as THREE from 'three';
import { RAD2DEG } from '../utils.js';

const _eye = new THREE.Vector3();
const _look = new THREE.Vector3();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _matrix = new THREE.Matrix4();
const _up = new THREE.Vector3(0, 1, 0);

// Full-frame sensor width (mm). A shot's `focal` is a lens on that body, and
// the frame is fitted to its width — so a narrower window crops height, as a
// 16:9 extraction from a 3:2 negative does, instead of quietly zooming.
const SENSOR_WIDTH = 36;

const EASE = {
  linear: (t) => t,
  // Quadratic ease-out: arrives at rest, so the cockpit glide (which starts from
  // a standstill) picks up without a velocity step.
  out: (t) => 1 - (1 - t) * (1 - t),
};

/**
 * First-run intro shots (CameraMode.INTRO) — a short list of scripted moves
 * around the parked A-10, each an eye path and a look-at path in the aircraft's
 * own frame (see SimConfig `INTRO.shots`). Hard cuts between shots; the dips
 * through black that soften them belong to the overlay (ui/IntroSequence.js),
 * which also decides when the clock runs — the camera holds the first frame of
 * the first shot until `play()`.
 *
 * Like the other cameras it only produces a position + quaternion that
 * CameraManager copies onto the shared camera, plus the lens via `fovFor`.
 */
export default class IntroCamera {
  constructor({ aircraft, shots }) {
    this.aircraft = aircraft;
    this.shots = shots;

    this.position = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();

    this.index = 0;
    this.time = 0;
    this.playing = false;
    /** Called with the new shot index on every cut. */
    this.onCut = null;
    // Timed callbacks on the shot clock (see `addCue`).
    this._cues = [];
  }

  /** Run `fn` once, `at` seconds into shot `shot` (or as soon as play passes it). */
  addCue(shot, at, fn) {
    this._cues.push({ shot, at, fn, fired: false });
  }

  get shot()       { return this.shots[this.index]; }
  get isLastShot() { return this.index >= this.shots.length - 1; }
  /** True once the last shot has run its full length. */
  get finished()   { return this.isLastShot && this.time >= this.shot.duration; }

  play() { this.playing = true; }

  /** Vertical FOV (degrees) for the current shot's focal length at `aspect`. */
  fovFor(aspect) {
    const focal = this.shot?.focal ?? 50;
    return 2 * Math.atan(SENSOR_WIDTH / (2 * focal) / aspect) * RAD2DEG;
  }

  update(dt) {
    const shot = this.shot;
    if (!shot || !this.aircraft) return;

    if (this.playing) {
      this.time += dt;
      if (this.time >= shot.duration && !this.isLastShot) {
        this.time -= shot.duration;
        this.index++;
        this.onCut?.(this.index);
        return this.update(0);
      }
      for (const c of this._cues) {
        if (!c.fired && (this.index > c.shot || (this.index === c.shot && this.time >= c.at))) {
          c.fired = true;
          c.fn();
        }
      }
    }

    const t = EASE[shot.ease || 'linear'](Math.min(this.time / shot.duration, 1));
    _eye.lerpVectors(_a.fromArray(shot.from), _b.fromArray(shot.to), t);
    _look.lerpVectors(_a.fromArray(shot.lookFrom), _b.fromArray(shot.lookTo), t);

    // Aircraft-local → world. The airframe is parked, but it does settle on its
    // gear for the first moments after spawn, and riding it keeps the framing
    // locked through that.
    const { position, quaternion } = this.aircraft;
    _eye.applyQuaternion(quaternion).add(position);
    _look.applyQuaternion(quaternion).add(position);

    this.position.copy(_eye);
    _matrix.lookAt(_eye, _look, _up);
    this.quaternion.setFromRotationMatrix(_matrix);
  }
}
